import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  UserPlus, 
  Check, 
  X, 
  Loader2,
  MessageSquare,
  UserMinus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Connections = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [connectionsRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/connections`, { withCredentials: true }),
        axios.get(`${API_URL}/api/connections/pending`, { withCredentials: true })
      ]);
      setConnections(connectionsRes.data);
      setPendingRequests(pendingRes.data);
    } catch (error) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (connectionId) => {
    setActionLoading(prev => ({ ...prev, [connectionId]: 'accept' }));
    try {
      await axios.put(`${API_URL}/api/connections/${connectionId}?action=accept`, {}, { withCredentials: true });
      toast.success('Connection accepted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to accept connection');
    } finally {
      setActionLoading(prev => ({ ...prev, [connectionId]: null }));
    }
  };

  const handleReject = async (connectionId) => {
    setActionLoading(prev => ({ ...prev, [connectionId]: 'reject' }));
    try {
      await axios.put(`${API_URL}/api/connections/${connectionId}?action=reject`, {}, { withCredentials: true });
      toast.success('Connection rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject connection');
    } finally {
      setActionLoading(prev => ({ ...prev, [connectionId]: null }));
    }
  };

  const handleRemove = async (connectionId) => {
    setActionLoading(prev => ({ ...prev, [connectionId]: 'remove' }));
    try {
      await axios.delete(`${API_URL}/api/connections/${connectionId}`, { withCredentials: true });
      toast.success('Connection removed');
      setConnections(connections.filter(c => c.connection_id !== connectionId));
    } catch (error) {
      toast.error('Failed to remove connection');
    } finally {
      setActionLoading(prev => ({ ...prev, [connectionId]: null }));
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'SALES': return 'bg-blue-100 text-blue-700';
      case 'HR': return 'bg-purple-100 text-purple-700';
      default: return 'bg-primary/10 text-primary';
    }
  };

  const ConnectionCard = ({ connection, isPending = false }) => {
    const otherUser = isPending ? connection.user : connection.user;
    const connectionId = connection.connection_id;

    return (
      <div className="connection-card" data-testid={`connection-card-${connectionId}`}>
        <Link to={`/profile/${otherUser.user_id}`}>
          <Avatar className="h-14 w-14">
            <AvatarImage src={otherUser.picture} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials(otherUser.name)}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <Link 
            to={`/profile/${otherUser.user_id}`}
            className="font-semibold hover:text-primary transition-colors"
          >
            {otherUser.name}
          </Link>
          {otherUser.headline && (
            <p className="text-sm text-muted-foreground truncate">{otherUser.headline}</p>
          )}
          <Badge variant="secondary" className={`mt-1 text-xs ${getRoleBadgeClass(otherUser.role)}`}>
            {otherUser.role}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {isPending ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleReject(connectionId)}
                disabled={actionLoading[connectionId]}
                className="rounded-full"
                data-testid={`reject-btn-${connectionId}`}
              >
                {actionLoading[connectionId] === 'reject' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
              </Button>
              <Button
                size="sm"
                onClick={() => handleAccept(connectionId)}
                disabled={actionLoading[connectionId]}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid={`accept-btn-${connectionId}`}
              >
                {actionLoading[connectionId] === 'accept' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Accept
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Link to={`/messages/${otherUser.user_id}`}>
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`message-btn-${connectionId}`}>
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </Link>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRemove(connectionId)}
                disabled={actionLoading[connectionId]}
                className="rounded-full text-muted-foreground hover:text-destructive"
                data-testid={`remove-btn-${connectionId}`}
              >
                {actionLoading[connectionId] === 'remove' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserMinus className="w-4 h-4" />
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">My Network</h1>
          <p className="text-muted-foreground">Manage your professional connections</p>
        </div>

        <Tabs defaultValue="connections" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="connections" className="flex items-center gap-2" data-testid="connections-tab">
              <Users className="w-4 h-4" />
              Connections
              {connections.length > 0 && (
                <Badge variant="secondary" className="ml-1">{connections.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-2" data-testid="pending-tab">
              <UserPlus className="w-4 h-4" />
              Pending
              {pendingRequests.length > 0 && (
                <Badge className="ml-1 bg-primary">{pendingRequests.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="connections">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : connections.length === 0 ? (
              <div className="empty-state">
                <Users className="w-16 h-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No connections yet</h3>
                <p className="text-muted-foreground mb-4">Start building your network!</p>
                <Link to="/search">
                  <Button className="rounded-full bg-primary hover:bg-primary/90">
                    Find People
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {connections.map((connection) => (
                  <ConnectionCard key={connection.connection_id} connection={connection} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="pending">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : pendingRequests.length === 0 ? (
              <div className="empty-state">
                <UserPlus className="w-16 h-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No pending requests</h3>
                <p className="text-muted-foreground">You're all caught up!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingRequests.map((request) => (
                  <ConnectionCard key={request.connection_id} connection={request} isPending />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Connections;
